import { useState, useEffect } from 'react'
import { Plus, Search, Edit, Trash2, X, Upload, Download, CalendarDays, Sparkles, Filter, Calendar, Globe, Building2, MapPin, Users, Info, AlertTriangle } from 'lucide-react'
import { holidaysAPI, districtsAPI, centersAPI } from '../../services/api'
import { useToast, getErrorMessage, extractBlobErrorData } from '../../components/Toast'
import { parsePaginatedResponse } from '../../lib/pagination'
import Pagination from '../../components/Pagination'
import { formatDate } from '../../lib/dateFormat'

const HOLIDAY_TYPES = ['NATIONAL', 'STATE', 'COMPANY', 'OPTIONAL']
const APPLICABLE_TO = [
  { value: 'ALL', label: 'All Employees' },
  { value: 'TRAINING_CENTRE', label: 'Particular Training Centre' },
  { value: 'DISTRICT', label: 'Particular District' },
]

const emptyForm = {
  name: '', date: '', holiday_type: 'COMPANY', description: '',
  applicable_to: 'ALL', training_centre: '', district: '', is_active: true,
}

const TYPE_STYLES: Record<string, string> = {
  NATIONAL: 'bg-blue-50 text-blue-700 ring-blue-200',
  STATE: 'bg-purple-50 text-purple-700 ring-purple-200',
  COMPANY: 'bg-neutral-100 text-neutral-700 ring-neutral-200',
  OPTIONAL: 'bg-amber-50 text-amber-700 ring-amber-200',
}

const TYPE_DOTS: Record<string, string> = {
  NATIONAL: 'bg-blue-500',
  STATE: 'bg-purple-500',
  COMPANY: 'bg-neutral-500',
  OPTIONAL: 'bg-amber-500',
}

const TYPE_LABELS: Record<string, string> = {
  NATIONAL: 'National',
  STATE: 'State',
  COMPANY: 'Company',
  OPTIONAL: 'Optional',
}

export default function HolidaysPage() {
  const { success, error: toastError } = useToast()
  const [holidays, setHolidays] = useState<any[]>([])
  const [districts, setDistricts] = useState<any[]>([])
  const [centers, setCenters] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const [searchTerm, setSearchTerm] = useState('')
  const [yearFilter, setYearFilter] = useState(String(new Date().getFullYear()))
  const [page, setPage] = useState(1)
  const [count, setCount] = useState(0)
  const [hasNext, setHasNext] = useState(false)
  const [hasPrev, setHasPrev] = useState(false)

  const [showModal, setShowModal] = useState(false)
  const [editingHoliday, setEditingHoliday] = useState<any | null>(null)
  const [form, setForm] = useState({ ...emptyForm })
  const [isSaving, setIsSaving] = useState(false)

  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadResult, setUploadResult] = useState<any | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  const load = async (pageArg = page) => {
    setIsLoading(true)
    try {
      const filters: any = {}
      if (searchTerm) filters.search = searchTerm
      if (yearFilter) filters.year = yearFilter
      const res = await holidaysAPI.list(pageArg, filters)
      const parsed = parsePaginatedResponse(res.data)
      setHolidays(parsed.results)
      setCount(parsed.count)
      setHasNext(parsed.hasNext)
      setHasPrev(parsed.hasPrev)
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to load holidays.'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load(page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, yearFilter])

  useEffect(() => {
    districtsAPI.list().then((res) => setDistricts(res.data.results || res.data)).catch(() => {})
    centersAPI.list().then((res) => setCenters(res.data.results || res.data)).catch(() => {})
  }, [])

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1)
      load(1)
    }, 300)
    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm])

  const openAddModal = () => {
    setEditingHoliday(null)
    setForm({ ...emptyForm })
    setShowModal(true)
  }

  const openEditModal = (holiday: any) => {
    setEditingHoliday(holiday)
    setForm({
      name: holiday.name,
      date: holiday.date,
      holiday_type: holiday.holiday_type,
      description: holiday.description || '',
      applicable_to: holiday.applicable_to,
      training_centre: holiday.training_centre ? String(holiday.training_centre) : '',
      district: holiday.district ? String(holiday.district) : '',
      is_active: holiday.is_active,
    })
    setShowModal(true)
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const payload: any = {
        name: form.name,
        date: form.date,
        holiday_type: form.holiday_type,
        description: form.description,
        applicable_to: form.applicable_to,
        is_active: form.is_active,
        training_centre: form.applicable_to === 'TRAINING_CENTRE' ? Number(form.training_centre) : null,
        district: form.applicable_to === 'DISTRICT' ? Number(form.district) : null,
      }
      if (editingHoliday) {
        await holidaysAPI.update(editingHoliday.id, payload)
        success('Holiday updated successfully.')
      } else {
        await holidaysAPI.create(payload)
        success('Holiday added successfully.')
      }
      setShowModal(false)
      await load(page)
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to save holiday.'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (holiday: any) => {
    if (!confirm(`Delete "${holiday.name}" (${formatDate(holiday.date)})?`)) return
    try {
      await holidaysAPI.delete(holiday.id)
      success('Holiday deleted.')
      await load(page)
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to delete holiday.'))
    }
  }

  const handleDownloadTemplate = async () => {
    try {
      const res = await holidaysAPI.downloadTemplate()
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'holiday_bulk_upload_template.xlsx')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err: any) {
      const blobData = await extractBlobErrorData(err)
      toastError(getErrorMessage(err, 'Failed to download template.', blobData))
    }
  }

  const handleBulkUpload = async () => {
    if (!uploadFile) return
    setIsUploading(true)
    try {
      const res = await holidaysAPI.bulkUpload(uploadFile)
      setUploadResult(res.data)
      success(`Bulk upload complete: ${res.data.success_count} added, ${res.data.error_count} failed.`)
      await load(page)
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Bulk upload failed.'))
    } finally {
      setIsUploading(false)
    }
  }

  const yearOptions = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 1 + i)

  const getApplicableLabel = (h: any) => {
    if (h.applicable_to === 'ALL') return { label: 'All Employees', icon: Users }
    if (h.applicable_to === 'TRAINING_CENTRE') return { label: h.training_centre_name || 'Training Centre', icon: Building2 }
    if (h.applicable_to === 'DISTRICT') return { label: h.district_name || 'District', icon: MapPin }
    return { label: '—', icon: Users }
  }

  const inputClass = "w-full px-3.5 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"
  const selectClass = `${inputClass} cursor-pointer`

  return (
    <div className="p-6 md:p-8 bg-[#FAF6EE] min-h-full">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-7">
        <div>
          <div className="inline-flex items-center gap-1.5 bg-[#E4670F]/10 text-[#C24A0A] border border-[#E4670F]/20 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase mb-2">
            <Sparkles className="w-3 h-3" />
            Calendar
          </div>
          <h1
            className="text-3xl font-bold text-neutral-900 mb-1"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Holiday Configuration
          </h1>
          <p className="text-sm text-neutral-500">Manage the organization's holiday calendar</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { setShowUploadModal(true); setUploadResult(null); setUploadFile(null) }}
            className="group flex items-center gap-2 px-4 py-2.5 border border-[#E4670F]/30 bg-[#E4670F]/[0.04] text-[#C24A0A] hover:bg-[#E4670F]/10 rounded-xl font-medium transition-all duration-300 hover:-translate-y-0.5"
          >
            <Upload className="w-4 h-4 transition-transform group-hover:scale-110" />
            Bulk Upload
          </button>
          <button
            onClick={openAddModal}
            className="group flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#A31621] to-[#7A1F1F] hover:from-[#8A1219] hover:to-[#5F1818] text-white rounded-xl font-medium transition-all duration-300 shadow-md shadow-[#A31621]/20 hover:shadow-lg hover:shadow-[#A31621]/30 hover:-translate-y-0.5"
          >
            <Plus className="w-4 h-4 transition-transform group-hover:rotate-90" />
            Add Holiday
          </button>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center">
            <Filter className="w-3.5 h-3.5 text-white" />
          </div>
          <h2 className="text-sm font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
            Filters
          </h2>
          {(searchTerm || yearFilter) && (
            <button
              onClick={() => { setSearchTerm(''); setYearFilter('') }}
              className="ml-auto text-xs font-medium text-neutral-500 hover:text-[#C24A0A] transition-colors"
            >
              Clear all
            </button>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 group">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
            <input
              type="text"
              placeholder="Search by holiday name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"
            />
          </div>
          <div className="relative group">
            <CalendarDays className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
            <select
              value={yearFilter}
              onChange={(e) => { setYearFilter(e.target.value); setPage(1) }}
              className="pl-10 pr-8 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all cursor-pointer appearance-none"
            >
              <option value="">All Years</option>
              {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* ── Results strip ── */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-sm text-neutral-600">
          <CalendarDays className="w-4 h-4 text-neutral-400" />
          <span>
            <span className="font-semibold text-neutral-900 tabular-nums">{count}</span>{' '}
            {count === 1 ? 'holiday' : 'holidays'}
          </span>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50/80 border-b border-neutral-200/70">
              <tr>
                <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Holiday</th>
                <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Date</th>
                <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Type</th>
                <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Applicable To</th>
                <th className="px-5 py-3.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Status</th>
                <th className="px-5 py-3.5 text-right text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-5 py-16">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-8 h-8 rounded-full border-2 border-neutral-200 border-t-[#E4670F] animate-spin" />
                      <p className="text-sm text-neutral-500">Loading holidays...</p>
                    </div>
                  </td>
                </tr>
              ) : holidays.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-16">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-14 h-14 rounded-2xl bg-neutral-100 flex items-center justify-center">
                        <CalendarDays className="w-7 h-7 text-neutral-400" />
                      </div>
                      <p className="text-sm font-medium text-neutral-700">No holidays found</p>
                      <p className="text-xs text-neutral-400">Try adjusting your filters or add a new holiday.</p>
                    </div>
                  </td>
                </tr>
              ) : holidays.map((h) => {
                const applicable = getApplicableLabel(h)
                const ApplicableIcon = applicable.icon
                return (
                  <tr
                    key={h.id}
                    className="border-b border-neutral-100 last:border-b-0 hover:bg-[#FAF6EE]/60 transition-colors"
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center shadow-sm flex-shrink-0">
                          <CalendarDays className="w-5 h-5 text-white" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-neutral-900 truncate max-w-[220px]">{h.name}</p>
                          {h.description && (
                            <p className="text-xs text-neutral-500 truncate max-w-[220px]">{h.description}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center gap-1.5 text-sm text-neutral-700 tabular-nums">
                        <Calendar className="w-3.5 h-3.5 text-neutral-400" />
                        {formatDate(h.date)}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ring-1 ${TYPE_STYLES[h.holiday_type] || 'bg-neutral-100 text-neutral-700 ring-neutral-200'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${TYPE_DOTS[h.holiday_type] || 'bg-neutral-400'}`} />
                        {TYPE_LABELS[h.holiday_type] || h.holiday_type}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm text-neutral-700">
                      <span className="inline-flex items-center gap-1.5">
                        <ApplicableIcon className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
                        <span className="truncate max-w-[180px]">{applicable.label}</span>
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                        h.is_active
                          ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                          : 'bg-neutral-100 text-neutral-600 ring-1 ring-neutral-200'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${h.is_active ? 'bg-emerald-500' : 'bg-neutral-400'}`} />
                        {h.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => openEditModal(h)}
                          title="Edit"
                          className="p-2 text-neutral-500 hover:text-[#C24A0A] hover:bg-[#E4670F]/10 rounded-lg transition-all"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(h)}
                          title="Delete"
                          className="p-2 text-neutral-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination page={page} onPageChange={setPage} count={count} hasNext={hasNext} hasPrev={hasPrev} />

      {/* ── Add/Edit Modal ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center shadow-md shadow-[#E4670F]/30">
                  {editingHoliday ? <Edit className="w-4 h-4 text-white" /> : <Plus className="w-4 h-4 text-white" />}
                </div>
                <h2 className="text-lg font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
                  {editingHoliday ? 'Edit Holiday' : 'Add Holiday'}
                </h2>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 overflow-y-auto space-y-3.5">
              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Holiday Name *</label>
                <input
                  type="text"
                  placeholder="e.g. Independence Day"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputClass}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Date *</label>
                <div className="relative group">
                  <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="w-full pl-10 pr-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Holiday Type *</label>
                <div className="relative group">
                  <Globe className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                  <select
                    value={form.holiday_type}
                    onChange={(e) => setForm({ ...form, holiday_type: e.target.value })}
                    className="w-full pl-10 pr-8 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all cursor-pointer appearance-none"
                  >
                    {HOLIDAY_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Description (optional)</label>
                <textarea
                  placeholder="Any additional details about this holiday..."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  className={`${inputClass} resize-none`}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Applicable To *</label>
                <div className="relative group">
                  <Users className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                  <select
                    value={form.applicable_to}
                    onChange={(e) => setForm({ ...form, applicable_to: e.target.value, training_centre: '', district: '' })}
                    className="w-full pl-10 pr-8 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all cursor-pointer appearance-none"
                  >
                    {APPLICABLE_TO.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                  </select>
                </div>
              </div>

              {form.applicable_to === 'TRAINING_CENTRE' && (
                <div>
                  <label className="block text-xs font-semibold text-neutral-600 mb-1.5">Training Centre *</label>
                  <div className="relative group">
                    <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                    <select
                      value={form.training_centre}
                      onChange={(e) => setForm({ ...form, training_centre: e.target.value })}
                      className="w-full pl-10 pr-8 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all cursor-pointer appearance-none"
                    >
                      <option value="">Select Training Centre</option>
                      {centers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {form.applicable_to === 'DISTRICT' && (
                <div>
                  <label className="block text-xs font-semibold text-neutral-600 mb-1.5">District *</label>
                  <div className="relative group">
                    <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 group-focus-within:text-[#E4670F] transition-colors pointer-events-none" />
                    <select
                      value={form.district}
                      onChange={(e) => setForm({ ...form, district: e.target.value })}
                      className="w-full pl-10 pr-8 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-900 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all cursor-pointer appearance-none"
                    >
                      <option value="">Select District</option>
                      {districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                </div>
              )}

              <label className="flex items-center gap-3 p-3 bg-neutral-50 border border-neutral-200 rounded-xl cursor-pointer hover:bg-neutral-100 transition-colors">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="w-4 h-4 rounded border-neutral-300 text-[#E4670F] focus:ring-[#E4670F] cursor-pointer"
                />
                <div>
                  <p className="text-sm font-medium text-neutral-800">Active</p>
                  <p className="text-xs text-neutral-500">Inactive holidays won't appear on employee calendars</p>
                </div>
              </label>
            </div>

            {/* Footer */}
            <div className="flex gap-3 px-6 py-4 border-t border-neutral-100 bg-neutral-50/60">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2.5 border border-neutral-200 bg-white rounded-xl font-medium text-neutral-700 hover:bg-neutral-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#A31621] to-[#7A1F1F] hover:from-[#8A1219] hover:to-[#5F1818] text-white rounded-xl font-medium disabled:opacity-50 transition-all shadow-md shadow-[#A31621]/20 hover:shadow-lg"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Bulk Upload Modal ── */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center shadow-md shadow-[#E4670F]/30">
                  <Upload className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-lg font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
                  Bulk Upload
                </h2>
              </div>
              <button
                onClick={() => setShowUploadModal(false)}
                className="p-2 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5">
              <button
                onClick={handleDownloadTemplate}
                className="group w-full mb-4 flex items-center justify-center gap-2 px-4 py-2.5 border border-[#E4670F]/30 bg-[#E4670F]/[0.04] text-[#C24A0A] hover:bg-[#E4670F]/10 rounded-xl font-medium transition-all"
              >
                <Download className="w-4 h-4 transition-transform group-hover:translate-y-0.5" />
                Download Sample Template
              </button>

              <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-xl px-3.5 py-2.5 text-xs text-blue-800 mb-4">
                <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>
                  Fill in the downloaded template, then upload it below. Rows with a duplicate date+scope
                  (within the file or already in the system) are skipped and reported as errors.
                </span>
              </div>

              <input
                type="file" accept=".xlsx"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-neutral-600 mb-4 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#E4670F]/10 file:text-[#C24A0A] hover:file:bg-[#E4670F]/20 file:cursor-pointer file:transition-colors"
              />

              {uploadResult && (
                <div className="mb-4">
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-2.5 text-center">
                      <p className="text-[10px] uppercase tracking-wide text-neutral-400 font-semibold">Total</p>
                      <p className="text-lg font-bold text-neutral-900 tabular-nums">{uploadResult.total_rows}</p>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-2.5 text-center">
                      <p className="text-[10px] uppercase tracking-wide text-emerald-600 font-semibold">Added</p>
                      <p className="text-lg font-bold text-emerald-700 tabular-nums">{uploadResult.success_count}</p>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-xl p-2.5 text-center">
                      <p className="text-[10px] uppercase tracking-wide text-red-600 font-semibold">Failed</p>
                      <p className="text-lg font-bold text-red-700 tabular-nums">{uploadResult.error_count}</p>
                    </div>
                  </div>

                  {uploadResult.row_errors?.length > 0 && (
                    <div className="border border-red-200 bg-red-50 rounded-xl max-h-48 overflow-y-auto">
                      <div className="sticky top-0 bg-red-100/90 backdrop-blur-sm px-3 py-2 font-semibold text-red-800 text-xs uppercase tracking-wide border-b border-red-200">
                        Upload Errors
                      </div>
                      {uploadResult.row_errors.map((re: any) => (
                        <div key={re.row} className="px-3 py-2.5 border-b border-red-100 last:border-b-0">
                          <div className="font-semibold text-red-700 text-sm">Row {re.row}</div>
                          <ul className="list-disc list-inside text-xs text-red-600 mt-1 space-y-0.5">
                            {re.errors.map((msg: string, i: number) => (
                              <li key={i}>{msg}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-3 px-6 py-4 border-t border-neutral-100 bg-neutral-50/60">
              <button
                onClick={() => setShowUploadModal(false)}
                className="flex-1 px-4 py-2.5 border border-neutral-200 bg-white rounded-xl font-medium text-neutral-700 hover:bg-neutral-50 transition-all"
              >
                Close
              </button>
              <button
                onClick={handleBulkUpload}
                disabled={!uploadFile || isUploading}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#A31621] to-[#7A1F1F] hover:from-[#8A1219] hover:to-[#5F1818] text-white rounded-xl font-medium disabled:opacity-50 transition-all shadow-md shadow-[#A31621]/20 hover:shadow-lg"
              >
                {isUploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}